import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOptionalAuth } from "../../auth/AuthContext";

/**
 * Background auto-refresh, controlled by the principal's General settings
 * (`autoRefreshEnabled` + `refreshIntervalSec`). Runs while any authenticated
 * page is mounted (it lives in the app shell) and refetches active queries.
 *
 * The cadence comes off `/me`, not from the Settings module: this hook runs on
 * EVERY authenticated page, so reading it from a privileged endpoint would make
 * a polling interval a privilege and silently disable auto-refresh for anyone
 * without `settings.read`.
 *
 * The YazamcoOps original additionally triggered a server-side WUD/Pulseway pull
 * for operators. Those modules are not part of this application, so only the
 * active-query refetch remains — the cadence itself is unchanged.
 */
const MIN_REFRESH_SEC = 30;

export function useBackgroundRefresh(): void {
  const preferences = useOptionalAuth()?.preferences;
  const queryClient = useQueryClient();
  const intervalMs =
    !preferences || !preferences.autoRefreshEnabled || preferences.refreshIntervalSec <= 0
      ? false
      : Math.max(MIN_REFRESH_SEC, preferences.refreshIntervalSec) * 1000;

  useEffect(() => {
    // `false` covers all the idle cases: auto-refresh off, interval 0, and
    // preferences not loaded yet.
    if (intervalMs === false) return;
    let running = false;
    const tick = async (): Promise<void> => {
      if (running) return; // skip if the previous cycle is still in flight
      running = true;
      try {
        await queryClient.refetchQueries({ type: "active" });
      } finally {
        running = false;
      }
    };
    const id = window.setInterval(() => void tick(), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, queryClient]);
}
